import axiosRetry from 'axios-retry';
import * as Sentry from '@sentry/react';

// Timeout constants
export const DEFAULT_TIMEOUT = 15000;   // 15s — generous for normal calls
export const UPLOAD_TIMEOUT = 120000;   // 120s — file uploads
export const POLL_TIMEOUT = 10000;      // 10s — background polling
export const REPORT_TIMEOUT = 120000;   // 120s — matches backend REPORT_TIMEOUT_MS for heavy report endpoints
export const TASK_AI_HINTS_REFRESH_TIMEOUT = 125000; // 125s — exceeds backend long-request timeout so API returns first

const IDEMPOTENT_METHODS = ['get', 'head', 'options', 'put', 'delete'];
const RETRYABLE_SERVER_STATUSES = [500, 502, 503, 504];

/**
 * Determine whether an error is retryable.
 *
 * Policy:
 *  - Network errors / timeouts (no response) → retry ALL methods
 *    Note: timeouts (ECONNABORTED) may have reached the server, but
 *    mutations are limited to 1 retry via the per-request retry count interceptor.
 *  - HTTP 429 (rate limited) → retry ALL methods, delay via Retry-After
 *  - HTTP 500, 502, non-maintenance 503, 504 → retry idempotent methods only
 *  - 401, 403, 404, 422, 400 → never retry (permanent client errors)
 *  - 503 with MAINTENANCE status → never retry (handled by redirect)
 *  - Cancelled requests → never retry
 *  - FormData uploads → never retry (handled via request interceptor flag)
 */
export function isRetryableError(error) {
    // Never retry cancelled requests
    if (error.__CANCEL__ || error.code === 'ERR_CANCELED') {
        return false;
    }

    // Never retry uploads (flag set by request interceptor)
    if (error.config?.__isUpload) {
        return false;
    }

    if (error.config?.['axios-retry']?.retries === 0) {
        return false;
    }

    // Network error or timeout (no response) → retry all methods
    if (!error.response) {
        return true;
    }

    const status = error.response.status;
    const method = (error.config?.method || '').toLowerCase();

    // 429 → retry all methods (server said "slow down", not "never")
    if (status === 429) {
        return true;
    }

    // 503 with MAINTENANCE body → don't retry, let redirect interceptor handle it
    if (status === 503 && error.response.data?.status === 'MAINTENANCE') {
        return false;
    }

    // 500, 502, non-maintenance 503, 504 → retry idempotent methods only
    if (RETRYABLE_SERVER_STATUSES.includes(status)) {
        return IDEMPOTENT_METHODS.includes(method);
    }

    return false;
}

/**
 * Calculate retry delay with exponential backoff + jitter.
 *
 * Pattern (matches SSEContext.js):
 *   baseDelay * 2^(retryCount - 1) ± 20% jitter
 *
 * GET/HEAD: 1s → 2s → 4s  (base 1000ms)
 * POST etc: 2s → 4s       (base 2000ms, but only 1 retry so just 2s)
 *
 * Respects Retry-After header when present (HTTP 429).
 */
export function retryDelay(retryCount, error) {
    // Respect Retry-After header (value in seconds)
    const retryAfter = error?.response?.headers?.['retry-after'];
    if (retryAfter) {
        const seconds = Number(retryAfter);
        if (!isNaN(seconds) && seconds > 0) {
            return seconds * 1000;
        }
    }

    const method = (error?.config?.method || '').toLowerCase();
    const isIdempotent = IDEMPOTENT_METHODS.includes(method);
    const baseDelay = isIdempotent ? 1000 : 2000;

    // Exponential: baseDelay * 2^(retryCount - 1)
    const exponentialDelay = baseDelay * Math.pow(2, retryCount - 1);

    // ±20% jitter to avoid thundering herd
    const jitter = exponentialDelay * 0.2 * (Math.random() * 2 - 1);

    return Math.round(exponentialDelay + jitter);
}

/**
 * Apply retry + timeout + upload detection to an axios instance.
 *
 * Interceptor ordering (critical):
 *   REQUEST:  [upload detector] → [existing auth interceptor]
 *   RESPONSE: [axios-retry]     → [existing 503/401/Sentry interceptor]
 *
 * Call this BEFORE adding response interceptors so retry sits first in chain.
 */
export function configureRetry(axiosInstance) {
    // Set default timeout (callers can still override per-request)
    axiosInstance.defaults.timeout = DEFAULT_TIMEOUT;

    // Request interceptor: detect FormData uploads → longer timeout, skip retry
    axiosInstance.interceptors.request.use((config) => {
        if (config.data instanceof FormData) {
            config.__isUpload = true;
            config.timeout = config.timeout === DEFAULT_TIMEOUT ? UPLOAD_TIMEOUT : config.timeout;
        }
        return config;
    });

    // Attach axios-retry
    axiosRetry(axiosInstance, {
        retries: 3,
        retryCondition: isRetryableError,
        retryDelay: (retryCount, error) => {
            const method = (error?.config?.method || '').toLowerCase();

            // Sentry breadcrumb so retry history is visible in error context
            Sentry.addBreadcrumb({
                category: 'api.retry',
                message: `Retry #${retryCount}: ${method.toUpperCase()} ${error?.config?.url}`,
                level: 'info',
                data: {
                    attempt: retryCount,
                    status: error?.response?.status || 'network_error',
                },
            });

            return retryDelay(retryCount, error);
        },
        // Override max retries per-request based on method
        shouldResetTimeout: true,
    });

    // Override the retry count dynamically: mutations get 1 retry, reads get 3
    axiosInstance.interceptors.request.use((config) => {
        const method = (config.method || '').toLowerCase();
        const isIdempotent = IDEMPOTENT_METHODS.includes(method);
        const retryConfig = config['axios-retry'] || {};
        const hasExplicitRetryCount = Number.isInteger(retryConfig.retries);
        config['axios-retry'] = {
            ...retryConfig,
            retries: hasExplicitRetryCount ? retryConfig.retries : isIdempotent ? 3 : 1,
        };
        return config;
    });
}
